import { create } from 'zustand';
import {
  Customer,
  DeletePaymentMethodResponse,
  PaymentMethod,
  SetupIntent,
} from '../generated/gql-types';
import { financialPartnerActions } from './financialPartner.action';

interface FinancialPartnerStore {
  // State
  customer: Customer | null;
  paymentMethods: PaymentMethod[] | null;
  isLoadingCustomer: boolean;
  isLoadingPaymentMethods: boolean;
  customerError: string | null;
  paymentMethodsError: string | null;

  // Actions
  setCustomer: (customer: Customer | null) => void;
  setPaymentMethods: (paymentMethods: PaymentMethod[] | null) => void;
  clearErrors: () => void;

  // Async actions
  fetchCustomerByTenantId: (tenantId: string) => Promise<Customer | null>;
  fetchPaymentMethodsByCustomerId: (
    customerId: string,
  ) => Promise<PaymentMethod[] | null>;
  fetchCustomerAndPaymentMethods: (tenantId: string) => Promise<void>;
  deletePaymentMethod: (
    paymentMethodId: string,
    tenantId: string,
  ) => Promise<DeletePaymentMethodResponse | null>;
  createSetupIntent: (customerId: string) => Promise<SetupIntent>;
}

const useFinancialPartnerStore = create<FinancialPartnerStore>((set, get) => ({
  // Initial state
  customer: null,
  paymentMethods: null,
  isLoadingCustomer: false,
  isLoadingPaymentMethods: false,
  customerError: null,
  paymentMethodsError: null,

  // Setters
  setCustomer: (customer) => set({ customer }),
  setPaymentMethods: (paymentMethods) => set({ paymentMethods }),
  clearErrors: () => set({ customerError: null, paymentMethodsError: null }),

  // Async actions
  fetchCustomerByTenantId: async (tenantId: string) => {
    set({ isLoadingCustomer: true, customerError: null });

    try {
      const customer =
        await financialPartnerActions.fetchCustomerByTenantId(tenantId);
      set({ customer, isLoadingCustomer: false });
      return customer;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch customer';
      set({
        customerError: errorMessage,
        isLoadingCustomer: false,
        customer: null,
      });
      return null;
    }
  },

  fetchPaymentMethodsByCustomerId: async (customerId: string) => {
    set({ isLoadingPaymentMethods: true, paymentMethodsError: null });

    try {
      const paymentMethods =
        await financialPartnerActions.fetchPaymentMethodsByCustomerId(
          customerId,
        );
      set({ paymentMethods, isLoadingPaymentMethods: false });
      return paymentMethods;
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to fetch payment methods';
      set({
        paymentMethodsError: errorMessage,
        isLoadingPaymentMethods: false,
        paymentMethods: null,
      });
      return null;
    }
  },

  fetchCustomerAndPaymentMethods: async (tenantId: string) => {
    const customer = await get().fetchCustomerByTenantId(tenantId);

    if (customer?.id) {
      await get().fetchPaymentMethodsByCustomerId(customer.id);
    } else {
      // Optionally set an error state or handle the case where no customer exists
      set({ customerError: 'No customer found for this tenant' });
    }
  },

  deletePaymentMethod: async (
    paymentMethodId: string,
    tenantId: string,
  ): Promise<DeletePaymentMethodResponse | null> => {
    try {
      // TODO-This would call your backend API to delete the payment method
      const response = await financialPartnerActions.deletePaymentMethod(
        paymentMethodId,
        tenantId,
      );

      if (!response || !response.success) {
        throw new Error('Failed to delete payment method');
      }

      set({
        paymentMethods: [],
      });

      return response;
    } catch (error) {
      console.error('Error deleting payment method:', error);
      return null;
    }
  },

  createSetupIntent: async (customerId: string) => {
    return await financialPartnerActions.createSetupIntent(customerId);
  },
}));

export default useFinancialPartnerStore;
